import {
  Modal,
  Box,
  FormControl,
  FormLabel,
  Divider,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";
import { LabelStyle } from "../../Common/styles/form";
import {
  ModalDrawerStyles,
  ModalHeader,
  ModalDrawerButtonStyles,
} from "../../Common/styles/modal";
import { InputWrapper } from "../../ParticipantRecords/ParticipantDetails/Medication/style";
import { DrawerForm } from "../../Common/styles/drawer";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { useParams } from "react-router-dom";
import http from "../../../utils/http";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { genderOptions } from "../../../const/participant";
import { countries } from "../../../const/countries";
import {
  isParticipantLocked,
  PARTICIPANT_LOCKED_MESSAGE,
} from "../../../utils/participantLock";

type Props = {
  showModal: boolean;
  closeModal: () => void;
  refreshPage: () => void;
  participantData: any;
};

const validationSchema = yup.object({
  firstName: yup.string().required("First name is required").trim(),
  lastName: yup.string().required("Last name is required").trim(),
  dob: yup.string().nullable(),
  gender: yup.string().nullable(),
  phoneNumber: yup.string().trim(),
  occupation: yup.string().trim(),
  addressLine1: yup.string().trim(),
  addressLine2: yup.string().trim(),
  city: yup.string().trim(),
  state: yup.string().trim(),
  country: yup.string().trim(),
  zipcode: yup.string().trim(),
  lastStudyDate: yup.string().trim(),
});

const EditParticipantDrawer = ({
  showModal,
  closeModal,
  refreshPage,
  participantData,
}: Props) => {
  const { participantId } = useParams();
  const isLocked = isParticipantLocked(participantData);

  const initialValues = {
    firstName: participantData?.firstName || "",
    lastName: participantData?.lastName || "",
    dob: participantData?.dob || "",
    gender: participantData?.gender || "",
    phoneNumber: participantData?.phoneNumber || "",
    occupation: participantData?.occupation || "",
    addressLine1: participantData?.address?.line1 || "",
    addressLine2: participantData?.address?.line2 || "",
    city: participantData?.address?.city || "",
    state: participantData?.address?.state || "",
    country: participantData?.address?.country || "",
    zipcode: participantData?.address?.zipcode || "",
    lastStudyDate: participantData?.lastStudyDate || "",
  };

  const onSubmit = async (values: any, { setSubmitting }: any) => {
    if (isLocked) {
      errorToastMessage(new Error(PARTICIPANT_LOCKED_MESSAGE));
      setSubmitting(false);
      return;
    }
    try {
      const body = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        dob: values.dob
          ? DateTime.fromFormat(values.dob, "MM/dd/yyyy").toFormat("yyyy-MM-dd")
          : undefined,
        gender: values.gender ? values.gender : undefined,
        phoneNumber: values.phoneNumber.trim(),
        occupation: values.occupation.trim(),

        line1: values.addressLine1.trim(),
        line2: values.addressLine2.trim(),
        city: values.city.trim(),
        state: values.state.trim(),
        country: values.country,
        zipcode: values.zipcode.trim(),

        lastStudyDate: values.lastStudyDate
          ? DateTime.fromFormat(values.lastStudyDate, "MM/dd/yyyy").toFormat(
              "yyyy-MM-dd"
            )
          : undefined,
      };

      const res = await http.patch(
        `/admin/registry/participants/${participantId}`,
        body
      );

      toastMessage("success", res.data.message);
      closeModal();
      refreshPage();
    } catch (error) {
      errorToastMessage(error as Error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title="Edit Participant Details"
          onCloseClick={closeModal}
        />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            setFieldValue,
          }) => (
            <Form style={DrawerForm}>
              <Box>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="firstName">
                    First Name
                  </FormLabel>
                  <TextField
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter first name"
                    variant="outlined"
                    fullWidth
                    error={touched.firstName && Boolean(errors.firstName)}
                  />
                  {touched.firstName && errors.firstName && (
                    <FormHelperText error>
                      {String(errors.firstName)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="lastName">
                    Last Name
                  </FormLabel>
                  <TextField
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter last name"
                    variant="outlined"
                    fullWidth
                    error={touched.lastName && Boolean(errors.lastName)}
                  />
                  {touched.lastName && errors.lastName && (
                    <FormHelperText error>
                      {String(errors.lastName)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="dob">
                    Date of Birth
                  </FormLabel>
                  <DatePicker
                    format="MM/dd/yyyy"
                    value={
                      values?.dob
                        ? DateTime.fromFormat(values?.dob, "MM/dd/yyyy")
                        : null
                    }
                    onChange={(newValue: any) => {
                      setFieldValue(
                        "dob",
                        newValue?.toFormat("MM/dd/yyyy") || ""
                      );
                    }}
                    slotProps={{
                      textField: {
                        inputProps: {
                          placeholder: "Select date of birth",
                          id: "dob",
                        },
                        fullWidth: true,
                        error: touched.dob && Boolean(errors.dob),
                        helperText:
                          touched.dob && errors.dob
                            ? (errors.dob as string)
                            : "",
                      },
                    }}
                  />
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }} fullWidth>
                  <FormLabel sx={LabelStyle} htmlFor="gender">
                    Gender
                  </FormLabel>
                  <Select
                    name="gender"
                    value={values.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.gender && Boolean(errors.gender)}
                  >
                    {genderOptions?.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.gender && errors.gender && (
                    <FormHelperText error>
                      {String(errors.gender)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="phoneNumber">
                    Phone Number
                  </FormLabel>
                  <TextField
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter phone number"
                    variant="outlined"
                    fullWidth
                    error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                  />
                  {touched.phoneNumber && errors.phoneNumber && (
                    <FormHelperText error>
                      {String(errors.phoneNumber)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="occupation">
                    Occupation
                  </FormLabel>
                  <TextField
                    name="occupation"
                    value={values.occupation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter occupation"
                    variant="outlined"
                    fullWidth
                    error={touched.occupation && Boolean(errors.occupation)}
                  />
                  {touched.occupation && errors.occupation && (
                    <FormHelperText error>
                      {String(errors.occupation)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="dob">
                    Last Study Date
                  </FormLabel>
                  <DatePicker
                    format="MM/dd/yyyy"
                    value={
                      values?.lastStudyDate
                        ? DateTime.fromFormat(
                            values?.lastStudyDate,
                            "MM/dd/yyyy"
                          )
                        : null
                    }
                    onChange={(newValue: any) => {
                      setFieldValue(
                        "lastStudyDate",
                        newValue?.toFormat("MM/dd/yyyy") || ""
                      );
                    }}
                    slotProps={{
                      textField: {
                        inputProps: {
                          placeholder: "Select date",
                          id: "lastStudyDate",
                        },
                        fullWidth: true,
                        error:
                          touched.lastStudyDate &&
                          Boolean(errors.lastStudyDate),
                        helperText:
                          touched.lastStudyDate && errors.lastStudyDate
                            ? (errors.lastStudyDate as string)
                            : "",
                      },
                    }}
                  />
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="addressLine1">
                    Address Line 1
                  </FormLabel>
                  <TextField
                    name="addressLine1"
                    value={values.addressLine1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter address line 1"
                    variant="outlined"
                    fullWidth
                    error={touched.addressLine1 && Boolean(errors.addressLine1)}
                  />
                  {touched.addressLine1 && errors.addressLine1 && (
                    <FormHelperText error>
                      {String(errors.addressLine1)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="addressLine2">
                    Address Line 2
                  </FormLabel>
                  <TextField
                    name="addressLine2"
                    value={values.addressLine2}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter address line 2"
                    variant="outlined"
                    fullWidth
                    error={touched.addressLine2 && Boolean(errors.addressLine2)}
                  />
                  {touched.addressLine2 && errors.addressLine2 && (
                    <FormHelperText error>
                      {String(errors.addressLine2)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="city">
                    City
                  </FormLabel>
                  <TextField
                    name="city"
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter city"
                    variant="outlined"
                    fullWidth
                    error={touched.city && Boolean(errors.city)}
                  />
                  {touched.city && errors.city && (
                    <FormHelperText error>{String(errors.city)}</FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="state">
                    State
                  </FormLabel>
                  <TextField
                    name="state"
                    value={values.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter state"
                    variant="outlined"
                    fullWidth
                    error={touched.state && Boolean(errors.state)}
                  />
                  {touched.state && errors.state && (
                    <FormHelperText error>
                      {String(errors.state)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="country">
                    Country
                  </FormLabel>
                  <Select
                    fullWidth
                    value={values.country || ""}
                    onChange={(e) => setFieldValue("country", e.target.value)}
                  >
                    {countries.map((country: any) => (
                      <MenuItem value={country.label} key={country.label}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.country && errors.country && (
                    <FormHelperText error>
                      {String(errors.country)}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="zipcode">
                    Zip Code
                  </FormLabel>
                  <TextField
                    name="zipcode"
                    value={values.zipcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter zip code"
                    variant="outlined"
                    fullWidth
                    error={touched.zipcode && Boolean(errors.zipcode)}
                  />
                  {touched.zipcode && errors.zipcode && (
                    <FormHelperText error>
                      {String(errors.zipcode)}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <Divider sx={{ mt: 2 }} />
                <Box sx={ModalDrawerButtonStyles}>
                  {!isSubmitting ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isLocked}
                        title={isLocked ? PARTICIPANT_LOCKED_MESSAGE : ""}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default EditParticipantDrawer;
